import { SupplierModel } from '@/models/supplier';
import { FilterColumnList } from '@/collections/filter_columns';
import { FilterColumnModel } from '@/models/filter_column';
import {
  rowsToInventoryMap,
  type InventoryByVariantId,
  type ReviewInventorySignals,
} from '@/utils/purchase-order/inventoryReportRowMapper';

export type { InventoryByVariantId, ReviewInventorySignals };

// Columns we explicitly request from the inventory_replenishment report.
// Keep this list as small as the report layer will accept — fewer columns
// trim the response and keep the Review mount snappy.
const REVIEW_INVENTORY_COLUMNS = [
  { code: 'variant_id',                       dataType: 'number', name: 'Variant ID' },
  { code: '[Actual Inventory Quantity]',      dataType: 'number', name: 'Stock' },
  { code: '[incoming_inventory_quantity]',    dataType: 'number', name: 'Incoming' },
  { code: '[stock_days_inventory_planning]',  dataType: 'number', name: 'Days Left' },
  { code: '[vendor_lead_time]',                dataType: 'number', name: 'Vendor Leadtime' },
];

export async function fetchInventoryByVariantIds(
  variantIds: number[],
): Promise<InventoryByVariantId> {
  const distinctIds = Array.from(new Set(
    (variantIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0),
  ));
  if (distinctIds.length === 0) { return {}; }

  const filters = new FilterColumnList();
  const variantFilter = new FilterColumnModel();
  variantFilter.mapData({
    name: 'Variant',
    code: 'variant_id',
    operator: 'in',
    value: distinctIds,
    dataType: 'number',
  });
  filters.add(variantFilter);

  // operator='all' bypasses supplier scoping (see fetchSupplierProductExpansion.ts:48
  // + supplier.ts:836). Review spans multiple vendors plus the unassigned bucket,
  // so supplier-level scoping doesn't apply.
  const supplier = new SupplierModel({ id: 0 });
  const report = await supplier.fetchProductsWithOptions(
    'inventory_replenishment',
    'all',
    {
      initialFilterColumns: filters,
      pageOptions: { page: 1, itemsPerPage: distinctIds.length },
      // Clone — helper mutates the columns array via .push() for supplier/supplier_id.
      columns: [...REVIEW_INVENTORY_COLUMNS],
      // Disable the helper's default `[inventory_replenishment] > 0` filter so
      // variants with no current reorder need still come back in the response.
      suggestFutureReorderQuantity: true,
    },
  );

  const rows: any[] = typeof (report as any)?.getReportRowsData === 'function'
    ? (report as any).getReportRowsData()
    : [];

  return rowsToInventoryMap(rows);
}
